import React, { useState, useEffect } from 'react';
import { Loader, AlertCircle } from 'lucide-react';
import SignatureCapture from './SignatureCapture';
import LivePhotoCapture from './LivePhotoCapture';
import SignatureSuccessModal from './SignatureSuccessModal';
import { signAgreement } from '../lib/agreements';
import { getAgreementFeeStatus, createAgreementFeeOrder, redirectToCheckout, formatINR } from '../lib/payment_helpers';

const PENDING_SIGNATURE_KEY = 'orello_pending_signature';

/**
 * Signing Flow
 * 1. Capture signature (draw or upload)
 * 2. Capture a live photo (camera, not upload — verifies the signer was present)
 * 3. Confirm consent
 * 4. If this party owes a share of the agreement fee, pay it first (redirects to
 *    Cashfree; the signature is submitted automatically on return - see PaymentStatus.jsx)
 * 5. Otherwise sign immediately
 * 6. Show success
 */
const SigningFlowWizard = ({
  agreementId = null,
  rentalId = null,
  userRole = 'tenant',
  userName = '',
  onSigningComplete = null,
  onCancel = null,
}) => {
  const [signatureData, setSignatureData] = useState(null);
  const [signatureUrl, setSignatureUrl] = useState(null);
  const [photoImage, setPhotoImage] = useState(null); // live-captured photo, base64 JPEG
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [signingComplete, setSigningComplete] = useState(false);
  const [signedDetails, setSignedDetails] = useState(null);

  const [feeStatus, setFeeStatus] = useState(null); // { requires_payment, amount_owed_paise }
  const [feeStatusLoading, setFeeStatusLoading] = useState(true);

  // Auto-position signature based on role
  const autoSignaturePosition = {
    tenant: { x: 100, y: 650, width: 150, height: 60 },
    landlord: { x: 450, y: 650, width: 150, height: 60 },
  }[userRole] || { x: 100, y: 650, width: 150, height: 60 };

  const loadFeeStatus = async () => {
    if (!agreementId) return;
    setFeeStatusLoading(true);
    try {
      const status = await getAgreementFeeStatus(agreementId);
      setFeeStatus(status);
    } catch (err) {
      // If we can't determine fee status, fail safe: assume payment may be required
      // and let the backend's own gate (402) be the final word when signing is attempted.
      console.error('Failed to load agreement fee status:', err);
      setFeeStatus(null);
    } finally {
      setFeeStatusLoading(false);
    }
  };

  useEffect(() => { loadFeeStatus(); }, [agreementId]);

  const buildSignaturePayload = () => ({
    signature_image: signatureUrl,                // base64 data URL of the signature
    photo_image: photoImage,                      // base64 JPEG, captured live at signing
    name_typed: signatureData?.name || userName,   // signer's name
    signature_id: signatureData?.signature_id,
    name: signatureData?.name || userName,
    signature_type: signatureData?.method || 'draw',
    signature_position: autoSignaturePosition,
    signature_url: signatureUrl,
  });

  // Direct sign — used when no fee is owed (or it's already paid).
  const handleSign = async () => {
    setIsSubmitting(true);
    setError(null);

    try {
      const result = await signAgreement(agreementId, buildSignaturePayload());

      setSignedDetails({
        agreementId,
        signedAt: new Date().toISOString(),
        userName: signatureData?.name || userName,
        userRole,
        signatureUrl,
        ...result,
      });
      setSigningComplete(true);

      if (onSigningComplete) {
        onSigningComplete(result);
      }
    } catch (err) {
      // Safety net: even if our pre-check said no payment was needed, the backend is
      // the final authority. If it says a fee is owed, refresh status and switch the
      // UI to the pay-then-sign path instead of just showing a dead-end error.
      if (err?.response?.status === 402) {
        await loadFeeStatus();
        setError(err.response?.data?.detail || 'Please pay your agreement fee share before signing.');
      } else {
        setError(err.message || 'Failed to sign agreement');
      }
      setIsSubmitting(false);
    }
  };

  // Pay-then-sign — stores the signature locally, redirects to Cashfree, and the
  // signature is submitted automatically once PaymentStatus.jsx confirms payment.
  const handlePayAndSign = async () => {
    setIsSubmitting(true);
    setError(null);

    try {
      sessionStorage.setItem(PENDING_SIGNATURE_KEY, JSON.stringify({
        agreementId,
        rentalId,
        payload: buildSignaturePayload(),
      }));

      const order = await createAgreementFeeOrder(agreementId);
      if (!order.session_id) throw new Error('Could not start payment.');

      await redirectToCheckout(order.session_id, order.cashfree_mode);
      // Navigation away — no further state updates needed here.
    } catch (err) {
      sessionStorage.removeItem(PENDING_SIGNATURE_KEY);
      setError(err.response?.data?.detail || err.message || 'Failed to start payment');
      setIsSubmitting(false);
    }
  };

  const requiresPayment = !!feeStatus?.requires_payment;
  const amountOwed = feeStatus?.amount_owed_paise || 0;

  // Success State
  if (signingComplete && signedDetails) {
    return (
      <SignatureSuccessModal
        agreementId={signedDetails.agreementId}
        signedAt={signedDetails.signedAt}
        userName={signedDetails.userName}
        userRole={signedDetails.userRole}
        signatureUrl={signedDetails.signatureUrl}
        onClose={onCancel}
        onDownloadPDF={() => {
          window.open(`/api/agreements/${agreementId}/pdf`, '_blank');
        }}
      />
    );
  }

  // Signing Flow
  return (
    <div className="w-full">
      <div className="space-y-6">
        {/* Header */}
        <div className="text-center mb-8">
          <h3 className="text-2xl font-bold text-white mb-2">✍️ Sign Agreement</h3>
          <p className="text-gray-400 text-sm">Your signature will be automatically placed</p>
        </div>

        {/* Signature Capture */}
        <div>
          <SignatureCapture
            initialName={userName}
            agreementId={agreementId}
            onSignatureCapture={(sig) => {
              setSignatureData(sig);
              if (sig.method === 'draw' || sig.method === 'upload') {
                setSignatureUrl(sig.image);
              }
            }}
            onClose={onCancel}
          />
        </div>

        {/* Live Photo Capture — required, camera only (no gallery upload) */}
        <LivePhotoCapture
          label="Live Photo (required to verify you are signing in person)"
          onCapture={setPhotoImage}
          onClear={() => setPhotoImage(null)}
        />

        {/* Agreement fee + Deposit protection fee notice */}
        {!feeStatusLoading && requiresPayment && (
          <div className="bg-orange-900/30 border border-orange-700 rounded-lg p-4">
            <p className="text-sm font-semibold text-orange-300 mb-3">💳 Agreement Fee Due</p>
            <div className="space-y-2 text-sm text-orange-200 mb-3 bg-orange-900/20 rounded p-3">
              {(feeStatus?.agreement_fee_paise || 0) > 0 && (
                <div className="flex justify-between">
                  <span>Agreement fee:</span>
                  <span className="font-medium">{formatINR(feeStatus.agreement_fee_paise)}</span>
                </div>
              )}
              {(feeStatus?.protection_fee_paise || 0) > 0 && (
                <>
                  <div className="flex justify-between">
                    <span>Deposit platform fee <span className="text-xs text-orange-300">(one time)</span>:</span>
                    <span className="font-medium">{formatINR(feeStatus.protection_fee_paise)}</span>
                  </div>
                  {(feeStatus?.protection_fee_gst_paise || 0) > 0 && (
                    <div className="flex justify-between">
                      <span>GST <span className="text-xs text-orange-300">(18% on platform fee)</span>:</span>
                      <span className="font-medium">{formatINR(feeStatus.protection_fee_gst_paise)}</span>
                    </div>
                  )}
                </>
              )}
              <div className="border-t border-orange-600 pt-2 flex justify-between font-semibold text-orange-100">
                <span>Your total:</span>
                <span className="text-orange-300">{formatINR(amountOwed)}</span>
              </div>
            </div>
            <p className="text-xs text-orange-300">
              You'll be taken to secure checkout, and your signature will be submitted automatically once payment is confirmed.
            </p>
          </div>
        )}

        {/* Consent */}
        <div className="bg-yellow-900/30 border border-yellow-700 rounded-lg p-4">
          <p className="text-sm font-semibold text-yellow-300 mb-2">⚠️ Confirm Before Signing</p>
          <ul className="space-y-1 text-sm text-yellow-200 ml-4">
            <li>✓ You have reviewed all terms and conditions</li>
            <li>✓ Information provided is accurate</li>
            <li>✓ You consent to sign electronically</li>
          </ul>
        </div>

        {/* Error */}
        {error && (
          <div className="bg-red-900/30 border border-red-700 rounded-lg p-4 flex gap-3">
            <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-red-200">{error}</p>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex gap-3 pt-4">
          <button
            onClick={onCancel}
            disabled={isSubmitting}
            className="flex-1 px-4 py-3 bg-gray-700 hover:bg-gray-600 text-white rounded-lg font-medium transition disabled:opacity-50"
          >
            ← Cancel
          </button>
          <button
            onClick={requiresPayment ? handlePayAndSign : handleSign}
            disabled={isSubmitting || !signatureData || !photoImage || feeStatusLoading}
            className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-orange-500 hover:bg-orange-600 text-white rounded-lg font-medium transition disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {(isSubmitting || feeStatusLoading) && <Loader className="w-4 h-4 animate-spin" />}
            {feeStatusLoading
              ? 'Checking...'
              : isSubmitting
              ? (requiresPayment ? 'Redirecting to payment...' : 'Signing...')
              : requiresPayment
              ? `Pay ${formatINR(amountOwed)} & Sign`
              : '✓ Confirm & Sign'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SigningFlowWizard;
